import { cn } from '@utils/cn';
import { getReadableTextColor } from '@utils/statusColors';
import React from 'react';

interface BadgeProps {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error';
  /**
   * Story-071 (sprint 14): an explicit palette hex, used instead of `variant`'s generic Tailwind
   * classes when a caller needs to render an exact status color (e.g. from `taskDisplay.ts`)
   * rather than one of the six fixed semantic variants. Existing `variant` behavior is unchanged
   * when `color` is omitted.
   */
  color?: string;
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  size = 'md',
  className = '',
  variant = 'default',
  color,
}) => {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs h-5', // 20px height
    md: 'px-3 py-1 text-sm h-6', // 24px height (most commonly used)
    lg: 'px-4 py-1.5 text-base h-8', // 32px height
  };

  const variantClasses = {
    default: 'bg-orange-100 dark:bg-orange-900 text-black dark:text-orange-100',
    primary: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100',
    secondary: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    success:
      'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100',
    warning:
      'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100',
    error: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100',
  };

  const baseClasses =
    'inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap w-fit';

  return (
    <span
      className={cn(
        baseClasses,
        sizeClasses[size],
        !color && variantClasses[variant],
        className,
      )}
      style={
        color
          ? { backgroundColor: color, color: getReadableTextColor(color) }
          : undefined
      }>
      {children}
    </span>
  );
};
